import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ObjectCustomerAiResultType, ObjectCustomerType } from '@econtractor/types';
import { useToast } from 'context/ToastContext';
import { getCloudflareUploadUrls } from 'api/apiCloudflare';
import { uploadFileWithProgress } from 'utils/helpers/featchFiles/uploadFileWithProgress';
import { runAiJobWithPolling } from 'utils/helpers/ai/runAiJobWithPolling';
import { hasCustomerAiValues } from './customerAiUtils';

type CustomerAiPhase = 'idle' | 'uploading' | 'processing';

interface UseCustomerAiInfoParams {
    onParsed: (parsed: Partial<ObjectCustomerType>) => void;
}

const useCustomerAiInfo = ({ onParsed }: UseCustomerAiInfoParams) => {
    const { t } = useTranslation(['Objects']);
    const { showToast } = useToast();
    const refInputFile = useRef<HTMLInputElement>(null);
    const [isBusy, setIsBusy] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | boolean>(false);
    const [phase, setPhase] = useState<CustomerAiPhase>('idle');
    const abortControllerRef = useRef<AbortController | null>(null);

    const processingLabel =
        phase === 'uploading' ? t('customerAi.loadingUploadFile') : t('customerAi.loadingAiProcess');

    const resetInput = () => {
        if (refInputFile.current) {
            refInputFile.current.value = '';
        }
    };

    const handleOpenClick = () => {
        if (isBusy) return;
        refInputFile.current?.click();
    };

    const handleCancel = () => {
        if (phase !== 'processing') return;
        abortControllerRef.current?.abort();
    };

    const handleChangeFile = async () => {
        const file = refInputFile.current?.files?.[0];
        if (!file) return;

        setIsBusy(true);
        setPhase('uploading');
        setUploadProgress(1);

        try {
            const filesMeta = [
                {
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    fileSize: file.size,
                },
            ];

            const uploadConfigs = await getCloudflareUploadUrls(filesMeta, {}, { keyPrefix: 'aitemp' });
            const uploadConfig = uploadConfigs?.[0];
            if (!uploadConfig) {
                throw new Error('cloud_upload_urls_missing');
            }

            const uploaded = await uploadFileWithProgress(file, uploadConfig, setUploadProgress);
            if (!uploaded.success || !uploaded.config?.publicUrl) {
                throw new Error('cloud_upload_failed');
            }

            setUploadProgress(100);
            setPhase('processing');
            abortControllerRef.current = new AbortController();

            const job = await runAiJobWithPolling<
                { fileUrl: string; fileName: string },
                ObjectCustomerAiResultType
            >({
                requestType: 'objects.customer.parse-file',
                payload: {
                    fileUrl: uploaded.config.publicUrl,
                    fileName: file.name,
                },
                pollIntervalMs: 2000,
                timeoutMs: 180000,
                signal: abortControllerRef.current.signal,
            });

            const parsedCustomer = job.result?.customer || {};
            if (!hasCustomerAiValues(parsedCustomer)) {
                showToast(t('customerAi.messEmpty'), 'warning');
                return;
            }

            onParsed(parsedCustomer);
            showToast(t('customerAi.messLoaded'), 'success');
        } catch (error) {
            const message = (error as Error)?.message || '';
            if (message === 'ai_job_aborted' || message === 'canceled') {
                showToast(t('customerAi.messCanceled'), 'warning');
            } else {
                console.error(error);
                showToast(t('customerAi.messParseError'), 'danger');
            }
        } finally {
            abortControllerRef.current = null;
            setIsBusy(false);
            setPhase('idle');
            setUploadProgress(false);
            resetInput();
        }
    };

    return {
        refInputFile,
        isBusy,
        uploadProgress,
        phase,
        processingLabel,
        handleOpenClick,
        handleChangeFile,
        handleCancel,
    };
};

export default useCustomerAiInfo;
